import { GlobeIcon, ShuffleIcon } from "lucide-react";
import { useCallback } from "react";

import { useSkillSharePinnedVersionData } from "./useSkillSharePinnedVersionData";

import { UrlBar } from "@/components/PromptTemplate/OverviewContent/SharePromptButton/UrlBar";
import {
  PinnedVersionSelector,
  ShareSessionCardToggle,
  SharedSessionCard,
  buildVersionUpdateFields,
  deriveSharedSessionVersionSelection,
  sessionUrl,
  type PinnedVersionSelection,
} from "@/components/SharedSessions";
import EntityIcon from "@/components/ui/icons/EntityIcon";
import { EntityType } from "@/queries/folder-entities/types";
import type { SharedSession } from "@/queries/prompts";

interface SessionCardProps {
  index: number;
  workspaceId: number;
  collectionId: string;
  session: SharedSession;
  onUpdate: (
    fields: Partial<
      Pick<SharedSession, "name" | "is_public" | "remix_enabled" | "label_name" | "version_number">
    >,
  ) => void;
  onDelete: () => void;
}

export const SessionCard = ({
  index,
  workspaceId,
  collectionId,
  session,
  onUpdate,
  onDelete,
}: SessionCardProps) => {
  const { versions, labels } = useSkillSharePinnedVersionData(workspaceId, collectionId);
  const versionSelection = deriveSharedSessionVersionSelection(session);

  const handleVersionChange = useCallback(
    (selection: PinnedVersionSelection) => {
      onUpdate(buildVersionUpdateFields(selection));
    },
    [onUpdate],
  );

  const leadingIcon = (
    <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-blue-50">
      <EntityIcon type={EntityType.SKILL_COLLECTION} size="xs" />
    </div>
  );

  const extraHeaderBadges = session.remix_enabled ? (
    <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400">
      <ShuffleIcon className="h-2.5 w-2.5" /> Remix
    </span>
  ) : null;

  const expandedSettings = (
    <>
      <UrlBar url={sessionUrl(session.share_token)} />
      <div className="mt-2 border-t border-gray-100 pt-1 dark:border-gray-700">
        <ShareSessionCardToggle
          icon={GlobeIcon}
          label="Public access"
          checked={session.is_public}
          onChange={value => onUpdate({ is_public: value })}
        />
        <PinnedVersionSelector
          variant="mini"
          value={versionSelection}
          onChange={handleVersionChange}
          versions={versions}
          labels={labels}
          selectTriggerClassName="h-6 w-[150px]"
        />
        <ShareSessionCardToggle
          icon={ShuffleIcon}
          label="Remixing"
          checked={session.remix_enabled}
          onChange={value => onUpdate({ remix_enabled: value })}
        />
      </div>
    </>
  );

  return (
    <SharedSessionCard
      animationIndex={index}
      session={session}
      onUpdate={onUpdate}
      onDelete={onDelete}
      leadingIcon={leadingIcon}
      extraHeaderBadges={extraHeaderBadges}
      expandedSettings={expandedSettings}
    />
  );
};
